import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useContent } from "../lib/content";
import { Building2, ArrowLeft, Mail, ExternalLink } from "lucide-react";

const looksLikeUrl = (s) => typeof s === "string" && /^https?:\/\//.test(s);
const looksLikeEmail = (s) => typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

export default function About() {
  const { tField } = useI18n();
  const { content } = useContent();
  const a = content?.about;

  const renderValue = (v) => {
    if (looksLikeEmail(v)) {
      return (
        <a href={`mailto:${v}`} className="text-[#3B82F6] hover:underline inline-flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" strokeWidth={1.75} />
          {v}
        </a>
      );
    }
    if (looksLikeUrl(v)) {
      return (
        <a href={v} target="_blank" rel="noreferrer" className="text-[#3B82F6] hover:underline inline-flex items-center gap-1.5">
          {v}
          <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
        </a>
      );
    }
    return <span className="text-white">{v}</span>;
  };

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-12 lg:py-20">
      <Link
        to="/"
        data-testid="about-back-link"
        className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-white mb-10"
      >
        <ArrowLeft className="h-3 w-3" strokeWidth={2} />
        Back
      </Link>

      <div className="flex items-start gap-4 mb-3">
        <div className="h-10 w-10 border border-[#3B82F6] text-[#3B82F6] flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">
            // ABOUT · OPERATOR
          </div>
          <h1
            data-testid="about-title"
            className="font-display text-4xl lg:text-5xl tracking-tight text-white"
            style={{ fontWeight: 800 }}
          >
            {tField(a?.title) || "About"}
          </h1>
        </div>
      </div>

      {a?.subtitle && (
        <p className="text-sm text-[#94A3B8] leading-relaxed max-w-3xl mt-8 mb-10">
          {tField(a.subtitle)}
        </p>
      )}

      <div className="bg-[#0F1525] border border-[#1E2A3F] divide-y divide-[#1E2A3F]">
        {(a?.rows || []).map((row, i) => (
          <div
            key={i}
            data-testid={`about-row-${i}`}
            className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-2 sm:gap-6 px-6 py-4 hover:bg-[#0A0E1A]/50 transition-colors"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] sm:py-1">
              {tField(row.label)}
            </div>
            <div className="text-sm leading-relaxed">{renderValue(tField(row.value))}</div>
          </div>
        ))}
      </div>

      {a?.footnote && (
        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-[#64748B] leading-relaxed">
          {tField(a.footnote)}
        </p>
      )}
    </div>
  );
}
